import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "glass shadow-[0_8px_32px_rgba(0,0,0,0.5)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <a href="#home" data-testid="nav-logo" aria-label="QT 2hype home" className="flex items-center gap-3">
            <span className="bg-white rounded-lg px-2 py-1.5 shadow-[0_0_18px_rgba(255,255,255,0.18)]">
              <img src="/images/qt-logo.png" alt="QT 2hype logo" className="h-8 w-auto" />
            </span>
            <span className="font-display font-800 text-lg tracking-tight">
              <span className="text-volt">QT</span> <span className="text-white">2HYPE</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-head text-sm font-semibold uppercase tracking-wider text-zinc-300 hover:text-volt transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#booking"
              data-testid="nav-book-cta"
              className="hidden sm:inline-flex items-center bg-volt text-ink font-head font-bold text-sm uppercase tracking-wider px-6 py-2.5 rounded-full hover:shadow-[0_0_30px_rgba(204,255,0,0.5)] transition-shadow"
            >
              Book Me
            </a>
            <button
              data-testid="nav-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-white"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-xl lg:hidden flex flex-col items-center justify-center gap-6"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-head text-3xl font-extrabold uppercase tracking-tight text-white hover:text-volt transition-colors"
              >
                {l.label}
              </motion.a>
            ))}
            <motion.a
              href="#booking"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-book-cta"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="mt-4 bg-volt text-ink font-head font-bold uppercase tracking-wider px-10 py-4 rounded-full shadow-[0_0_35px_rgba(204,255,0,0.4)]"
            >
              Book {BRAND.name}
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
